import { MIN_GAIN, type AudioVoice } from "./constants";
import { applyEnvelope, releaseEnvelope } from "./envelope";
import { createFilteredNoise } from "./noise";
import { renderYidakiDrone } from "./models/droneTractModels";

export interface DroneOptions {
  id: string;
  variantId?: string;
  frequency: number;
  gain?: number;
  waveform?: OscillatorType;
  mouthX?: number;
  mouthY?: number;
  circularHold?: boolean;
  vocalization?: number;
}

export function createDrone(context: AudioContext, options: DroneOptions): AudioVoice {
  if (options.id.includes("yidaki") || options.id.includes("didgeridoo")) {
    return renderYidakiDrone({
      context,
      destination: context.createGain(),
      instrumentId: "yidaki_didgeridoo",
      variantId: options.variantId ?? (options.id.includes("traditional_yidaki") ? "traditional_yidaki" : "modern_didgeridoo"),
      frequency: options.frequency,
      velocity: Math.min(1, Math.max(0.05, options.gain ?? 0.34)),
      duration: 8,
      noteLabel: "drone",
      ornament: options.circularHold ? "circular_breath" : "drone",
      controls: {
        mouthX: options.mouthX,
        mouthY: options.mouthY,
        circularHold: options.circularHold,
        vocalization: options.vocalization,
        droneLevel: options.gain ?? 0.34
      },
      now: context.currentTime,
      quality: "standard"
    });
  }

  const oscillator = context.createOscillator();
  const gain = context.createGain();
  const formant = context.createBiquadFilter();
  const vocalLayer = context.createOscillator();
  const vocalGain = context.createGain();
  const lipBuzz = context.createOscillator();
  const breathNoise = createFilteredNoise(context, 900 + (options.mouthX ?? 0.4) * 1900, 1.8, 2);
  const breathGain = context.createGain();
  const mouthX = Math.min(1, Math.max(0, options.mouthX ?? 0.4));
  const mouthY = Math.min(1, Math.max(0, options.mouthY ?? 0.35));
  const now = context.currentTime;

  oscillator.type = options.waveform ?? "sawtooth";
  oscillator.frequency.setValueAtTime(options.frequency, now);

  vocalLayer.type = "sine";
  vocalLayer.frequency.setValueAtTime(options.frequency * (2.2 + mouthY * 1.6), now);

  lipBuzz.type = "sawtooth";
  lipBuzz.frequency.setValueAtTime(options.frequency * (1.01 + mouthX * 0.04), now);

  formant.type = "bandpass";
  formant.frequency.setValueAtTime(420 + mouthX * 2200, now);
  formant.Q.setValueAtTime(2 + mouthY * 10, now);

  vocalGain.gain.value = (options.circularHold ? 0.055 : 0.028) + Math.min(1, Math.max(0, options.vocalization ?? 0)) * 0.055;
  breathGain.gain.value = 0.018 + mouthY * 0.034;
  gain.gain.value = MIN_GAIN;

  oscillator.connect(formant);
  lipBuzz.connect(formant);
  formant.connect(gain);
  vocalLayer.connect(vocalGain);
  vocalGain.connect(gain);
  breathNoise.source.loop = true;
  breathNoise.filter.connect(breathGain);
  breathGain.connect(gain);

  applyEnvelope(gain.gain, context, {
    attack: 0.08,
    decay: 0.2,
    sustain: 0.92,
    release: 0.8,
    peakGain: options.gain ?? 0.26
  });

  oscillator.start();
  vocalLayer.start();
  lipBuzz.start();
  breathNoise.source.start();

  return {
    id: options.id,
    output: gain,
    gain,
    sources: [oscillator, vocalLayer, lipBuzz, breathNoise.source],
    cleanupNodes: [formant, vocalGain, breathNoise.filter, breathGain],
    stop: (when = context.currentTime) => {
      releaseEnvelope(gain.gain, context, 0.55, when);
      oscillator.stop(when + 0.7);
      vocalLayer.stop(when + 0.7);
      lipBuzz.stop(when + 0.7);
      breathNoise.source.stop(when + 0.7);
    }
  };
}
